/**
 * API Wallet Provider
 *
 * Implements WalletDataProvider using the BFF API with axios.
 * Used for production and staging environments.
 */

import type {
	ChecklistItemSnake,
	Comment,
	TestResult,
	Transaction,
	Wallet,
} from "../../../types";
import axiosClient from "../../api/axiosClient";
import type { WalletDataProvider } from "../types";

interface PaginatedResponse<T> {
	data: T[];
	pagination: {
		page: number;
		limit: number;
		total: number;
		totalPages: number;
	};
}

/** Returns true if error is an API error with an optional specific status code. */
export function isApiError(error: unknown, status?: number): boolean {
	if (!error || typeof error !== "object") return false;
	if (!("status" in error)) return false;
	if (status === undefined) return true;
	return (error as { status: number }).status === status;
}

/** DB checklist shape - BFF may store camelCase or snake_case depending on request */
type DBChecklistItem = {
	completed?: boolean;
	completed_at?: string;
	completed_by?: string;
	completedAt?: string;
	completedBy?: string;
	document_url?: string;
	document_name?: string;
	uploaded_at?: string;
	documentUrl?: string;
	documentName?: string;
	uploadedAt?: string;
};

/**
 * DBWallet format (snake_case from BFF) to Wallet format (camelCase for UI)
 */
interface DBWallet {
	id: string;
	wallet_name: string;
	company_name: string;
	chain: string;
	asset: string;
	custodial_type: "Custodial" | "Non-Custodial" | "Hybrid";
	products: string[];
	integration_status: string;
	onboarding_status: string;
	is_whitelisted: boolean;
	whitelist_environments?: string[];
	main_contact_name?: string;
	main_contact_email?: string;
	main_contact_phone?: string;
	main_contact_role?: string;
	slack_channel?: string;
	salesforce_case_number?: string;
	home_domain?: string;
	funding_address?: string;
	deposit_address?: string;
	authentication_address?: string;
	agent_id?: string;
	main_office_id?: string;
	conducts_kyc?: boolean;
	mau?: number;
	country_of_incorporation?: string;
	priority_markets?: string[];
	excluded_countries?: string[];
	toml_file_domain?: string;
	production_home_domain?: string;
	production_funding_address?: string;
	production_deposit_address?: string;
	production_auth_address?: string;
	production_toml_file_domain?: string;
	production_custodial_type?: string;
	created_at: string;
	updated_at: string;
	created_by?: string | null;
	updated_by?: string | null;
	test_results?: TestResult[];
	proposed_changes?: Record<string, unknown> | null;
	proposed_changes_by?: string | null;
	proposed_changes_at?: string | null;
	kyc_form_completed?: {
		kyb_form_id?: string;
		kyb_progress?: number;
		completed?: boolean;
		completed_at?: string;
		completed_by?: string;
		kyb_status?: string; // Enriched: draft | submitted | approved | rejected
	} | null;
	// Partner Tracker fields (already camelCase-style nested objects from BFF)
	customer_success?: {
		kam?: string;
		tier?: 1 | 2 | 3;
		meeting_frequency?: string;
		tasks_progress?: number;
		missing_items_count?: number;
	};
	marketing_grant?: {
		total_grant_usd?: number;
		funds_left?: number;
		grant_expiration_date?: string;
		grant_status?: "Active" | "Expired" | "Pending" | "Completed";
		denomination?: string;
		quarter_year?: string;
		geos?: string[];
		agreement_link?: string;
	};
	partner_tasks?: Array<{
		task_name: string;
		deadline?: string;
		dri?: string;
		progress: "pending" | "in_progress" | "completed";
		comments?: string;
	}>;
	use_case?: string;
	ramping_type?: "On-Ramping" | "Off-Ramping" | "Both";
	is_existing_partner?: boolean;
	// Checklist items (snake_case keys from BFF) - read from API to persist check status
	integration_completed?: DBChecklistItem | null;
	salesforce_opportunity_created?: DBChecklistItem | null;
	nda_signed?: DBChecklistItem | null;
	consent_form_signed?: DBChecklistItem | null;
	contract_signed?: DBChecklistItem | null;
	salesforce_case_created?: DBChecklistItem | null;
	slack_channel_created?: DBChecklistItem | null;
	partner_activated?: DBChecklistItem | null;
	testing_completed?: DBChecklistItem | null;
	whitelisted_in_production?: DBChecklistItem | null;
	go_live_verification?: DBChecklistItem | null;
	fees_pricing_contacted?: DBChecklistItem | null;
	marketing_follow_up?: DBChecklistItem | null;
}

/** Merge default checklist item with DB values so check status persists after refetch */
function mergeChecklistItem(
	defaultItem: DBChecklistItem,
	dbItem?: DBChecklistItem | null,
): DBChecklistItem {
	if (!dbItem) return defaultItem;
	return {
		...defaultItem,
		completed: dbItem.completed ?? defaultItem.completed,
		completedAt:
			dbItem.completed_at ?? dbItem.completedAt ?? defaultItem.completedAt,
		completedBy:
			dbItem.completed_by ?? dbItem.completedBy ?? defaultItem.completedBy,
		documentUrl:
			dbItem.document_url ?? dbItem.documentUrl ?? defaultItem.documentUrl,
		documentName:
			dbItem.document_name ?? dbItem.documentName ?? defaultItem.documentName,
		uploadedAt:
			dbItem.uploaded_at ?? dbItem.uploadedAt ?? defaultItem.uploadedAt,
	};
}

/**
 * Transform DBWallet (snake_case) to Wallet (camelCase)
 */
function transformDBWalletToWallet(dbWallet: DBWallet): Wallet {
	return {
		id: dbWallet.id,
		wallet_name: dbWallet.wallet_name,
		company_name: dbWallet.company_name,
		chain: dbWallet.chain as Wallet["chain"],
		asset: dbWallet.asset as Wallet["asset"],
		custodial_type: dbWallet.custodial_type as Wallet["custodial_type"],
		products: dbWallet.products as Wallet["products"],
		integration_status:
			dbWallet.integration_status as Wallet["integration_status"],
		onboarding_status:
			dbWallet.onboarding_status as Wallet["onboarding_status"],
		is_existing_partner: dbWallet.is_existing_partner,
		is_whitelisted: dbWallet.is_whitelisted,
		whitelist_environments:
			dbWallet.whitelist_environments as Wallet["whitelist_environments"],
		main_contact_name: dbWallet.main_contact_name,
		main_contact_email: dbWallet.main_contact_email,
		main_contact_phone: dbWallet.main_contact_phone,
		main_contact_role: dbWallet.main_contact_role,
		slack_channel: dbWallet.slack_channel,
		salesforce_case_number: dbWallet.salesforce_case_number,
		home_domain: dbWallet.home_domain,
		funding_address: dbWallet.funding_address,
		deposit_address: dbWallet.deposit_address,
		authentication_address: dbWallet.authentication_address,
		agent_id: dbWallet.agent_id,
		main_office_id: dbWallet.main_office_id,
		conducts_kyc: dbWallet.conducts_kyc,
		mau: dbWallet.mau,
		country_of_incorporation: dbWallet.country_of_incorporation,
		priority_markets: dbWallet.priority_markets,
		excluded_countries: dbWallet.excluded_countries,
		production_home_domain: dbWallet.production_home_domain,
		production_funding_address: dbWallet.production_funding_address,
		production_deposit_address: dbWallet.production_deposit_address,
		production_auth_address: dbWallet.production_auth_address,
		production_toml_file_domain:
			dbWallet.production_toml_file_domain ?? dbWallet.toml_file_domain,
		production_custodial_type: dbWallet.production_custodial_type as
			| "Custodial"
			| "Non-Custodial"
			| "Both"
			| undefined,
		proposed_changes: dbWallet.proposed_changes ?? undefined,
		proposed_changes_by: dbWallet.proposed_changes_by ?? undefined,
		proposed_changes_at: dbWallet.proposed_changes_at ?? undefined,
		// Checklist items - merge with DB values so check status persists after refetch
		salesforce_opportunity_created: mergeChecklistItem(
			{
				completed: false,
			},
			dbWallet.salesforce_opportunity_created,
		),
		nda_signed: mergeChecklistItem({ completed: false }, dbWallet.nda_signed),
		consent_form_signed: mergeChecklistItem(
			{ completed: false },
			dbWallet.consent_form_signed,
		),
		kyc_form_completed: (() => {
			const k = dbWallet.kyc_form_completed as
				| {
						kyb_form_id?: string;
						kyb_progress?: number;
						kybFormId?: string;
						kybProgress?: number;
						completed?: boolean;
						completed_at?: string;
						completed_by?: string;
						completedAt?: string;
						completedBy?: string;
						kyb_status?: string;
				  }
				| undefined
				| null;
			if (!k) {
				return {
					completed: false,
				};
			}
			return {
				completed: k.completed ?? false,
				kyb_form_id: k.kyb_form_id ?? k.kybFormId,
				kyb_progress: k.kyb_progress ?? k.kybProgress,
				completed_at: k.completed_at ?? k.completedAt,
				completed_by: k.completed_by ?? k.completedBy,
				kyb_status: k.kyb_status,
			};
		})(),
		contract_signed: mergeChecklistItem(
			{ completed: false },
			dbWallet.contract_signed,
		),
		salesforce_case_created: mergeChecklistItem(
			{ completed: false },
			dbWallet.salesforce_case_created,
		),
		partner_setup_notified:
			((dbWallet as unknown as Record<string, unknown>)
				.partner_setup_notified as boolean | undefined) ?? false,
		slack_channel_created: mergeChecklistItem(
			{ completed: false },
			dbWallet.slack_channel_created,
		),
		integration_completed: mergeChecklistItem(
			{
				completed: false,
			},
			dbWallet.integration_completed,
		),
		partner_activated: mergeChecklistItem(
			{ completed: false },
			dbWallet.partner_activated,
		),
		has_own_fees:
			((dbWallet as unknown as Record<string, unknown>).has_own_fees as
				| boolean
				| undefined) ?? false,
		fee_structures: (
			((dbWallet as unknown as Record<string, unknown>).fee_structures as Array<
				Record<string, unknown>
			>) ?? []
		).map((f) => ({
			startRange: (f.start_range ?? f.startRange) as number,
			endRange: (f.end_range ?? f.endRange) as number,
			fee: f.fee as number,
			feeType:
				((f.fee_type ?? f.feeType) as "percentage" | "flat") ?? "percentage",
		})),
		fees_pricing_contacted: mergeChecklistItem(
			{ completed: false },
			dbWallet.fees_pricing_contacted,
		),
		testing_completed: mergeChecklistItem(
			{ completed: false },
			dbWallet.testing_completed,
		),
		product_type: undefined,
		test_results: dbWallet.test_results ?? [],
		whitelisted_in_production: mergeChecklistItem(
			{ completed: false },
			dbWallet.whitelisted_in_production,
		),
		go_live_verification: mergeChecklistItem(
			{
				completed: false,
			},
			dbWallet.go_live_verification,
		),
		marketing_follow_up: mergeChecklistItem(
			{ completed: false },
			dbWallet.marketing_follow_up,
		),
		transactions: [],
		comments: [],
		created_at: dbWallet.created_at,
		updated_at: dbWallet.updated_at,

		// Partner Tracker fields
		customer_success: dbWallet.customer_success
			? {
					kam: dbWallet.customer_success.kam,
					tier: dbWallet.customer_success.tier,
					meeting_frequency: dbWallet.customer_success.meeting_frequency as
						| "Every two weeks"
						| "Monthly"
						| "Quarterly"
						| "As needed"
						| undefined,
					tasks_progress: dbWallet.customer_success.tasks_progress,
					missing_items_count: dbWallet.customer_success.missing_items_count,
				}
			: undefined,
		marketing_grant: dbWallet.marketing_grant
			? {
					total_grant_usd: dbWallet.marketing_grant.total_grant_usd,
					funds_left: dbWallet.marketing_grant.funds_left,
					grant_expiration_date: dbWallet.marketing_grant.grant_expiration_date,
					grant_status: dbWallet.marketing_grant.grant_status,
					denomination: dbWallet.marketing_grant.denomination,
					quarter_year: dbWallet.marketing_grant.quarter_year,
					geos: dbWallet.marketing_grant.geos,
					agreement_link: dbWallet.marketing_grant.agreement_link,
				}
			: undefined,
		partner_tasks: dbWallet.partner_tasks?.map((t) => ({
			task_name: t.task_name,
			deadline: t.deadline,
			dri: t.dri,
			progress: t.progress,
			comments: t.comments,
		})),
		use_case: dbWallet.use_case,
		ramping_type: dbWallet.ramping_type,
	};
}

const cleanLink = (link: string | undefined): string | undefined => {
	if (!link || link === "#" || link === "undefined" || link.trim() === "")
		return undefined;
	return link;
};

/**
 * Apply migration transforms to a wallet: clean stale link values, ensure subtask defaults.
 * Called once at the API boundary so it never runs on re-renders.
 */
export function migrateWallet(
	wallet: ReturnType<typeof transformDBWalletToWallet>,
): ReturnType<typeof transformDBWalletToWallet> {
	return {
		...wallet,
		nda_signed: {
			...(wallet.nda_signed || {}),
			link: cleanLink(wallet.nda_signed?.link),
			subtasks: wallet.nda_signed?.subtasks || { sent: false, received: false },
		} as typeof wallet.nda_signed,
		consent_form_signed: {
			...(wallet.consent_form_signed || {}),
			link: cleanLink(wallet.consent_form_signed?.link),
			subtasks: wallet.consent_form_signed?.subtasks || {
				sent: false,
				received: false,
			},
		} as typeof wallet.consent_form_signed,
		kyc_form_completed: {
			...(wallet.kyc_form_completed || {}),
			link: cleanLink(wallet.kyc_form_completed?.link),
			subtasks: wallet.kyc_form_completed?.subtasks || {
				sent: false,
				received: false,
			},
		} as typeof wallet.kyc_form_completed,
		contract_signed: {
			...(wallet.contract_signed || {}),
			link: cleanLink(wallet.contract_signed?.link),
			subtasks: wallet.contract_signed?.subtasks || {
				sent: false,
				received: false,
			},
		} as typeof wallet.contract_signed,
	};
}

export const apiWalletProvider: WalletDataProvider = {
	async getAll(): Promise<Wallet[]> {
		// Fetch all wallets across pages (BFF defaults to limit=20)
		const allWallets: DBWallet[] = [];
		let page = 1;
		const limit = 100; // BFF max

		while (true) {
			// Interceptor returns PaginatedResponse as-is (no success wrapper)
			const response = (await axiosClient.get(
				`/api/wallets?page=${page}&limit=${limit}`,
				{ timeout: 60000 },
			)) as unknown as PaginatedResponse<DBWallet>;

			if (response && "data" in response && Array.isArray(response.data)) {
				allWallets.push(...response.data);
				const pagination = response.pagination;
				if (!pagination || page >= pagination.totalPages) break;
				page++;
			} else if (Array.isArray(response)) {
				// Fallback: assume it's already an array (backward compatibility)
				allWallets.push(...(response as unknown as DBWallet[]));
				break;
			} else {
				break;
			}
		}

		return allWallets.map((w) => migrateWallet(transformDBWalletToWallet(w)));
	},

	async getWalletsForUser(userId: string): Promise<Wallet[]> {
		// Axios interceptor unwraps { success, data } so we get the payload directly
		const data = (await axiosClient.get(
			`/api/users/${userId}/wallets`,
		)) as unknown as DBWallet[];
		if (Array.isArray(data)) {
			return data.map((w) =>
				migrateWallet(transformDBWalletToWallet(w as DBWallet)),
			);
		}
		return [];
	},

	async getById(id: string): Promise<Wallet | null> {
		try {
			// Interceptor unwraps { success, data } — result is the DBWallet directly
			const data = (await axiosClient.get(
				`/api/wallets/${id}`,
			)) as unknown as DBWallet;
			if (!data) return null;
			return migrateWallet(transformDBWalletToWallet(data));
		} catch (error: unknown) {
			if (isApiError(error, 404)) {
				return null;
			}
			throw error;
		}
	},

	/**
	 * @deprecated Internal portal writes use tRPC wallet.create. REST kept for external clients.
	 */
	async create(wallet: Wallet): Promise<Wallet> {
		// Transform Wallet to CreateWalletRequest format
		const requestBody = {
			walletName: wallet.wallet_name,
			companyName: wallet.company_name,
			chain: wallet.chain,
			asset: wallet.asset,
			custodialType: wallet.custodial_type,
			products: wallet.products,
			mainContactName: wallet.main_contact_name,
			mainContactEmail: wallet.main_contact_email,
			mainContactPhone: wallet.main_contact_phone,
			mainContactRole: wallet.main_contact_role,
			priorityMarkets: wallet.priority_markets,
			countryOfIncorporation: wallet.country_of_incorporation,
			mau: wallet.mau,
			conductsKyc: wallet.conducts_kyc,
			homeDomain: wallet.home_domain,
			// Include status fields for existing partners
			...(wallet.is_existing_partner && {
				integrationStatus: wallet.integration_status,
				onboardingStatus: wallet.onboarding_status,
				isExistingPartner: wallet.is_existing_partner,
				isWhitelisted: wallet.is_whitelisted,
				whitelistEnvironments: wallet.whitelist_environments,
			}),
		};
		// Interceptor unwraps { success, data } — result is the wallet directly
		const data = (await axiosClient.post(
			"/api/wallets",
			requestBody,
		)) as unknown as Wallet;
		if (!data) throw new Error("No wallet data returned from API");
		return data;
	},

	/**
	 * @deprecated Internal portal writes use tRPC wallet.update. REST kept for external clients.
	 */
	async update(id: string, updates: Partial<Wallet>): Promise<Wallet> {
		// Convert snake_case to camelCase for REST (BFF expects camelCase)
		const requestBody: Record<string, unknown> = {};

		// Basic fields
		if (updates.wallet_name !== undefined)
			requestBody.walletName = updates.wallet_name;
		if (updates.company_name !== undefined)
			requestBody.companyName = updates.company_name;
		if (updates.chain !== undefined) requestBody.chain = updates.chain;
		if (updates.asset !== undefined) requestBody.asset = updates.asset;
		if (updates.custodial_type !== undefined)
			requestBody.custodialType = updates.custodial_type;
		if (updates.products !== undefined) requestBody.products = updates.products;
		const integrationStatus =
			updates.integration_status ??
			(updates as { integrationStatus?: string }).integrationStatus;
		if (integrationStatus !== undefined)
			requestBody.integrationStatus = integrationStatus;
		if (updates.onboarding_status !== undefined)
			requestBody.onboardingStatus = updates.onboarding_status;
		if (updates.is_existing_partner !== undefined)
			requestBody.isExistingPartner = updates.is_existing_partner;

		// Main contact (flattened)
		if (
			updates.main_contact_name !== undefined ||
			updates.main_contact_email !== undefined
		) {
			requestBody.mainContactName = updates.main_contact_name;
			requestBody.mainContactEmail = updates.main_contact_email;
			requestBody.mainContactPhone = updates.main_contact_phone;
			requestBody.mainContactRole = updates.main_contact_role;
		}

		// Technical Information
		if (updates.slack_channel !== undefined)
			requestBody.slackChannel = updates.slack_channel;
		if (updates.home_domain !== undefined)
			requestBody.homeDomain = updates.home_domain;
		if (updates.funding_address !== undefined)
			requestBody.fundingAddress = updates.funding_address;
		if (updates.deposit_address !== undefined)
			requestBody.depositAddress = updates.deposit_address;
		if (updates.authentication_address !== undefined)
			requestBody.authenticationAddress = updates.authentication_address;
		if (updates.agent_id !== undefined) requestBody.agentId = updates.agent_id;
		if (updates.main_office_id !== undefined)
			requestBody.mainOfficeId = updates.main_office_id;

		// Optional fields
		if (updates.conducts_kyc !== undefined)
			requestBody.conductsKyc = updates.conducts_kyc;
		if (updates.mau !== undefined) requestBody.mau = updates.mau;
		if (updates.country_of_incorporation !== undefined)
			requestBody.countryOfIncorporation = updates.country_of_incorporation;
		if (updates.priority_markets !== undefined)
			requestBody.priorityMarkets = updates.priority_markets;
		if (updates.excluded_countries !== undefined)
			requestBody.excludedCountries = updates.excluded_countries;

		// Whitelisting
		if (updates.is_whitelisted !== undefined)
			requestBody.isWhitelisted = updates.is_whitelisted;
		if (updates.whitelist_environments !== undefined)
			requestBody.whitelistEnvironments = updates.whitelist_environments;

		// Production whitelisting fields
		if (updates.production_home_domain !== undefined)
			requestBody.productionHomeDomain = updates.production_home_domain;
		if (updates.production_funding_address !== undefined)
			requestBody.productionFundingAddress = updates.production_funding_address;
		if (updates.production_deposit_address !== undefined)
			requestBody.productionDepositAddress = updates.production_deposit_address;
		if (updates.production_auth_address !== undefined)
			requestBody.productionAuthAddress = updates.production_auth_address;
		if (updates.production_toml_file_domain !== undefined)
			requestBody.productionTomlFileDomain =
				updates.production_toml_file_domain;
		if (updates.production_custodial_type !== undefined)
			requestBody.productionCustodialType = updates.production_custodial_type;
		if (updates.salesforce_case_number !== undefined)
			requestBody.salesforceCaseNumber = updates.salesforce_case_number;

		if (updates.test_results !== undefined)
			requestBody.testResults = updates.test_results;

		// KYB form link (wallet → form) for resume flow
		if (updates.kyc_form_completed !== undefined)
			requestBody.kycFormCompleted = updates.kyc_form_completed;

		// Workflow flags and fee structures
		if (updates.has_own_fees !== undefined)
			requestBody.hasOwnFees = updates.has_own_fees;
		if (updates.partner_setup_notified !== undefined)
			requestBody.partnerSetupNotified = updates.partner_setup_notified;
		if (updates.fee_structures !== undefined)
			requestBody.feeStructures = updates.fee_structures;

		// Forward any checklist/other keys not explicitly handled (BFF accepts them)
		for (const [k, v] of Object.entries(updates)) {
			if (v !== undefined && !(k in requestBody)) requestBody[k] = v;
		}

		// Interceptor unwraps { success, data } — result is the wallet directly
		const data = (await axiosClient.patch(
			`/api/wallets/${id}`,
			requestBody,
		)) as unknown as Wallet;
		if (!data) throw new Error("No wallet data returned from API");
		return data;
	},

	async delete(id: string): Promise<void> {
		await axiosClient.delete(`/api/wallets/${id}`);
	},

	async applyProposal(walletId: string): Promise<Wallet> {
		// Interceptor unwraps { success, data } — result is the wallet directly
		const data = (await axiosClient.post(
			`/api/wallets/${walletId}/proposals/apply`,
			{},
		)) as unknown as Wallet;
		if (!data) throw new Error("No wallet data returned from API");
		return data;
	},

	async rejectProposal(walletId: string): Promise<Wallet> {
		// Interceptor unwraps { success, data } — result is the wallet directly
		const data = (await axiosClient.post(
			`/api/wallets/${walletId}/proposals/reject`,
			{},
		)) as unknown as Wallet;
		if (!data) throw new Error("No wallet data returned from API");
		return data;
	},

	async addComment(walletId: string, comment: Comment): Promise<Wallet> {
		const requestBody = {
			text: comment.text,
			isInternal: (comment as unknown as Record<string, unknown>).isInternal,
		};
		// Interceptor unwraps { success, data } — result is the wallet directly
		const data = (await axiosClient.post(
			`/api/wallets/${walletId}/comments`,
			requestBody,
		)) as unknown as Wallet;
		if (!data) throw new Error("No wallet data returned from API");
		return data;
	},

	async updateChecklist(
		walletId: string,
		checklistKey: keyof Wallet,
		updates: Partial<
			ChecklistItemSnake & { completed_at?: string; completed_by?: string }
		>,
	): Promise<Wallet> {
		// Checklist items are updated via the main wallet PATCH endpoint
		// BFF expects camelCase keys — convert snake_case key and updates
		const snakeToCamelKey: Record<string, string> = {
			integration_completed: "integrationCompleted",
			salesforce_opportunity_created: "salesforceOpportunityCreated",
			nda_signed: "ndaSigned",
			consent_form_signed: "consentFormSigned",
			kyc_form_completed: "kycFormCompleted",
			contract_signed: "contractSigned",
			salesforce_case_created: "salesforceCaseCreated",
			slack_channel_created: "slackChannelCreated",
			partner_activated: "partnerActivated",
			fees_pricing_contacted: "feesPricingContacted",
			testing_completed: "testingCompleted",
			whitelisted_in_production: "whitelistedInProduction",
			go_live_verification: "goLiveVerification",
			marketing_follow_up: "marketingFollowUp",
		};
		const camelKey =
			snakeToCamelKey[checklistKey as string] ?? (checklistKey as string);
		const camelUpdates: Record<string, unknown> = {
			...(updates.completed !== undefined && { completed: updates.completed }),
			...(updates.completed_at !== undefined && {
				completedAt: updates.completed_at,
			}),
			...(updates.completed_by !== undefined && {
				completedBy: updates.completed_by,
			}),
			...(updates.document_url !== undefined && {
				documentUrl: updates.document_url,
			}),
			...(updates.document_name !== undefined && {
				documentName: updates.document_name,
			}),
			...(updates.uploaded_at !== undefined && {
				uploadedAt: updates.uploaded_at,
			}),
		};
		const requestBody: Record<string, unknown> = {
			[camelKey]: camelUpdates,
		};
		// Interceptor unwraps { success, data } — result is the wallet directly
		const data = (await axiosClient.patch(
			`/api/wallets/${walletId}`,
			requestBody,
		)) as unknown as Wallet;
		if (!data) throw new Error("No wallet data returned from API");
		return data;
	},

	async addTransaction(
		walletId: string,
		transaction: Transaction,
	): Promise<Wallet> {
		// Map UI Transaction (id, type, referenceNumber, timestamp) to BFF shape
		const requestBody = {
			referenceNumber: transaction.referenceNumber,
			direction: transaction.type === "Deposit" ? "cash-in" : "cash-out",
			amount: 0,
			currency: "USD",
			status: "complete",
			notes: "",
		};
		await axiosClient.post(
			`/api/wallets/${walletId}/transactions`,
			requestBody,
		);
		// API returns transaction, not wallet; invalidation triggers refetch
		return {} as Wallet;
	},

	async getTransactions(walletId: string): Promise<Transaction[]> {
		// Interceptor unwraps { success, data } — result is the array directly
		const data = (await axiosClient.get(
			`/api/wallets/${walletId}/transactions`,
		)) as unknown as Array<{
			id: string;
			transaction_id: string;
			direction: string;
			recorded_at: string;
		}>;
		if (!Array.isArray(data)) return [];
		return data.map((t) => ({
			id: t.id,
			type:
				t.direction === "cash-in"
					? ("Deposit" as const)
					: ("Withdraw" as const),
			referenceNumber: t.transaction_id,
			timestamp: t.recorded_at,
		}));
	},

	async deleteTransaction(
		walletId: string,
		transactionId: string,
	): Promise<Wallet> {
		// Interceptor unwraps { success, data } — result is the wallet directly
		const data = (await axiosClient.delete(
			`/api/transactions/${transactionId}?walletId=${encodeURIComponent(walletId)}`,
		)) as unknown as Wallet;
		return data;
	},
};
